import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

import PerfectScrollbar from 'react-perfect-scrollbar';
import { useTheme } from '@emotion/react';
import { IconButton, TableContainer, TextField, Collapse, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import {
  CardActions,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  TablePagination
} from '@mui/material';
import CategoryChip from '../../UI/CategoryChip';
import useClasses from '../../../helpers/useClasses';

const styles = theme => ({
  root: {},
  content: {
    padding: 0
  },
  actions: {
    justifyContent: 'flex-end'
  },
  iconButton: {
    padding: 10,
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    width: '40%'
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  brokerRow: {
    cursor: 'pointer'
  },
  brokerCell: {
    paddingTop: 0,
    paddingBottom: 0
  },
  expandCell: {
    width: 52,
    paddingTop: 0,
    paddingBottom: 0
  },
  subCell: {
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: '#f7fafc'
  },
  subWrap: {
    padding: theme.spacing(1, 2, 2, 6)
  },
  countBadge: {
    display: 'inline-block',
    minWidth: 28,
    padding: '2px 10px',
    borderRadius: 12,
    backgroundColor: '#eef2f6',
    fontWeight: 500,
    textAlign: 'center'
  },
  merchantName: {
    fontWeight: 500
  },
  merchantDba: {
    color: theme.palette.text.secondary,
    fontSize: '12px'
  },
  noteCell: {
    color: theme.palette.text.secondary,
    wordBreak: 'break-word'
  },
  dateCell: {
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary
  },
  nowrap: {
    whiteSpace: 'nowrap'
  },
  merchantLink: {
    color: theme.palette.text.link,
    textDecoration: 'none'
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2)
  },
  emptyRow: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary
  }
});

const BrokersTable = props => {
  const {
    brokers,
    pagination,
    onPageChange,
    onPerPageChange,
    onSearchClick,
    onSearchCancelClick,
    currentSearch,
    isFiltered,
    order,
    orderBy,
    onRequestSort,
    broker_notes,
    notes_loading,
    onExpandBroker
  } = props;

  const classes = useClasses(styles);
  const [search, setSearch] = useState(currentSearch);
  const [expanded, setExpanded] = useState({});

  const handleSearchChange = event => {
    setSearch(event.target.value);
  };

  const handleToggle = (broker_name) => {
    const isOpen = !!expanded[broker_name];
    if (!isOpen && !broker_notes[broker_name]) {
      onExpandBroker(broker_name);
    }
    setExpanded({ ...expanded, [broker_name]: !isOpen });
  };

  const styles2 = theme => ({
    root: {
      flexShrink: 0,
      marginLeft: theme.spacing(2.5),
    },
  });

  const TablePaginationActions = (props) => {
    const paginationClasses = useClasses(styles2);
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = event => {
      onPageChange(event, 0);
    };
    const handleBackButtonClick = event => {
      onPageChange(event, page - 1);
    };
    const handleNextButtonClick = event => {
      onPageChange(event, page + 1);
    };
    const handleLastPageButtonClick = event => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
      <div className={paginationClasses.root}>
        <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
          {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
          {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </div>
    );
  };

  const renderSubRow = (broker) => {
    const isOpen = !!expanded[broker.broker_name];
    if (!isOpen) {
      return null;
    }

    const notes = broker_notes[broker.broker_name];
    const loading = notes_loading[broker.broker_name];

    return (
      <TableRow>
        <TableCell className={classes.subCell} colSpan={4}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <div className={classes.subWrap}>
              {loading ? (
                <div className={classes.loadingRow}>
                  <CircularProgress size={22} />
                </div>
              ) : notes && notes.length > 0 ? (
                <Table size="small" sx={{ backgroundColor: '#fff', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '22%' }} />{/* Merchant */}
                    <col style={{ width: '12%' }} />{/* EIN — fixed XX-XXXXXXX */}
                    <col style={{ width: '24%' }} />{/* Category — fits widest chip label */}
                    <col style={{ width: '28%' }} />{/* Note — free text, wraps */}
                    <col style={{ width: '14%' }} />{/* Date Added — MMM DD, YYYY */}
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell>Merchant</TableCell>
                      <TableCell>EIN</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Note</TableCell>
                      <TableCell>Date Added</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notes.map(note => (
                      <TableRow hover key={note.resource_id || note.id}>
                        <TableCell>
                          <Typography className={classes.merchantName} variant="body2">
                            {note.merchant_resource_id ? (
                              <Link className={classes.merchantLink} to={`/merchants/${note.merchant_resource_id}`}>
                                {note.merchant_legal_name}
                              </Link>
                            ) : (
                              note.merchant_legal_name
                            )}
                          </Typography>
                          {note.merchant_dba ? (
                            <Typography className={classes.merchantDba}>DBA {note.merchant_dba}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell className={classes.nowrap}>{note.merchant_fein}</TableCell>
                        <TableCell><CategoryChip category={note.category} /></TableCell>
                        <TableCell className={classes.noteCell}>{note.note}</TableCell>
                        <TableCell className={classes.dateCell}>{moment(note.created_at).format('MMM DD, YYYY')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography className={classes.emptyRow} variant="body2">No notes found for this broker.</Typography>
              )}
            </div>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <React.Fragment>
      <CardContent sx={{ padding: 0 }}>
        <PerfectScrollbar>
          <TableContainer className={classes.inner}>
            <Table>
              <TableHead sx={{ backgroundColor: '#fafafa' }}>
                <TableRow>
                  <TableCell colSpan={4}>
                    <TextField
                      className={classes.searchInput}
                      placeholder="Search by broker name"
                      inputProps={{ 'aria-label': 'Search by broker name' }}
                      value={search}
                      onChange={(event) => handleSearchChange(event)}
                      size='small'
                    />
                    <IconButton
                      type="submit"
                      className={classes.iconButton}
                      onClick={(event) => onSearchClick(event, search)}
                      aria-label="search">
                      <SearchIcon />
                    </IconButton>
                    { isFiltered ?
                      <IconButton
                        type="submit"
                        className={classes.iconButton}
                        onClick={(event) => { setSearch(''); onSearchCancelClick(event); }}
                        aria-label="clear search">
                        <CloseIcon />
                      </IconButton>
                    : null }
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableHead sx={{ backgroundColor: '#fafafa' }}>
                <TableRow>
                  <TableCell className={classes.expandCell}></TableCell>
                  <TableCell sortDirection={orderBy === 'broker_name' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'broker_name'}
                      direction={orderBy === 'broker_name' ? order : 'asc'}
                      onClick={() => onRequestSort('broker_name')}
                    >
                      Broker
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align={'right'} sortDirection={orderBy === 'note_count' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'note_count'}
                      direction={orderBy === 'note_count' ? order : 'desc'}
                      onClick={() => onRequestSort('note_count')}
                    >
                      Number of Notes
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align={'right'} sortDirection={orderBy === 'last_note_at' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'last_note_at'}
                      direction={orderBy === 'last_note_at' ? order : 'desc'}
                      onClick={() => onRequestSort('last_note_at')}
                    >
                      Last Note Added On
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brokers && brokers.length > 0 ? brokers.map(broker => (
                  <React.Fragment key={broker.broker_name}>
                    <TableRow
                      hover
                      className={classes.brokerRow}
                      onClick={() => handleToggle(broker.broker_name)}
                    >
                      <TableCell className={classes.expandCell}>
                        <IconButton size="small" aria-label="expand row">
                          {expanded[broker.broker_name] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell className={classes.brokerCell}>
                        <div className={classes.nameContainer}>
                          <Typography className={classes.merchantName} variant="body1">
                            {broker.broker_name}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell className={classes.brokerCell} align={'right'}>
                        <span className={classes.countBadge}>{broker.note_count}</span>
                      </TableCell>
                      <TableCell className={`${classes.brokerCell} ${classes.nowrap}`} align={'right'}>
                        {broker.last_note_at ? moment(broker.last_note_at).format('MMM DD, YYYY') : ''}
                      </TableCell>
                    </TableRow>
                    {renderSubRow(broker)}
                  </React.Fragment>
                )) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography className={classes.emptyRow} variant="body2">No brokers found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PerfectScrollbar>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={pagination ? pagination.total_count : 0}
          onPageChange={onPageChange}
          onChangeRowsPerPage={onPerPageChange}
          page={pagination ? pagination.current_page : 0}
          rowsPerPage={pagination ? pagination.per_page : 25}
          rowsPerPageOptions={[25, 50, 100]}
          ActionsComponent={TablePaginationActions}
        />
      </CardActions>
    </React.Fragment>
  );
};

export default BrokersTable;
